import { RulesManager, type RulesManagerProps } from '@carboncopyco/editor-ui';
import { IFindAndReplaceRuleType } from '@carboncopyco/types';
import { useMemo, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorSdkProvider';
import { AddFindAndReplaceButton } from './AddFindAndReplaceButton';
import type { FindAndReplaceManagerContextProps } from './FindAndReplaceManagerContext';
import { FindAndReplaceManagerContext } from './FindAndReplaceManagerContext';
import { FindAndReplaceRulesContent } from './FindAndReplaceRulesContent';

export type FindAndReplaceManagerProps = Pick<
  RulesManagerProps,
  'isOpen' | 'onClose' | 'onLearnMoreClick'
> &
  FindAndReplaceManagerContextProps;

const helpLink = 'https://help.demostack.com/en/articles/5919246';
const supportedFindAndReplaceModes = [
  IFindAndReplaceRuleType.replaceImage,
  IFindAndReplaceRuleType.replaceLink,
];

export const FindAndReplaceManager = ({
  isOpen,
  onClose,
  rules,
  variables,
  createRule,
  editRule,
  deleteRule,
  reorderRules,
  onLearnMoreClick,
}: FindAndReplaceManagerProps) => {
  const editorSdk = useEditor();
  const contentRef = useRef<HTMLDivElement>();
  const [ruleToCreate, setRuleToCreate] = useState<IFindAndReplaceRuleType>();

  const showEmptyState = !rules.length && !ruleToCreate;

  const contextValue = useMemo(
    () => ({
      rules,
      variables,
      createRule: async (rule) => {
        const rules = await createRule(rule);
        setRuleToCreate(null);
        return rules;
      },
      editRule,
      deleteRule,
      reorderRules,
    }),
    [rules, variables, createRule, editRule, deleteRule, reorderRules]
  );

  return (
    <FindAndReplaceManagerContext.Provider value={contextValue}>
      <RulesManager
        isOpen={isOpen}
        onClose={!ruleToCreate ? onClose : undefined}
        onLearnMoreClick={onLearnMoreClick}
      >
        <RulesManager.Header>
          <RulesManager.Header.Title label="Find & Replace" />
          <RulesManager.Header.Action>
            <AddFindAndReplaceButton
              onSelect={(ruleType) => {
                if (supportedFindAndReplaceModes.includes(ruleType)) {
                  editorSdk.startFindAndReplaceMode({
                    ruleType,
                  });
                } else {
                  setRuleToCreate(ruleType);
                  contentRef.current?.scrollTo({ top: 0 });
                }
              }}
            />
          </RulesManager.Header.Action>
        </RulesManager.Header>
        {!showEmptyState && (
          <div className="mt-2 mb-5">
            <RulesManager.Subheader
              label="Replace elements globally."
              linkLabel="Learn how"
              linkUrl={helpLink}
            />
          </div>
        )}
        {showEmptyState ? (
          <RulesManager.EmptyState>
            <RulesManager.EmptyState.Title label="Replace elements globally" />
            <RulesManager.EmptyState.Subtitle
              label="Not sure how it works?"
              linkLabel="Learn more"
              linkUrl={helpLink}
            />
          </RulesManager.EmptyState>
        ) : (
          <RulesManager.Content ref={contentRef}>
            <FindAndReplaceRulesContent
              ruleToCreate={ruleToCreate}
              cancelCreation={() => setRuleToCreate(null)}
            />
          </RulesManager.Content>
        )}
      </RulesManager>
    </FindAndReplaceManagerContext.Provider>
  );
};
